import { FC } from 'react';
import Drawer from '@mui/material/Drawer';
import { Divider, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';

type DrawerProps = {
  open: boolean;
  handleClose: () => void;
  children: any;
  closeButton?: boolean;
  width?: number | string;
  title: any;
  dropBack?: boolean;
  noPadding?: boolean;
  flexColumn?: boolean;
  /** Kept for callers; drawers always fill the viewport and overlap the app header. */
  overlapHeader?: boolean;
};
const DrawerMenu: FC<DrawerProps> = ({
  open,
  handleClose,
  children,
  closeButton = false,
  dropBack = true,
  width = 500,
  title,
  noPadding = false,
  flexColumn = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = isSmallScreen ? '100%' : width;
  const drawerZIndex = theme.zIndex.modal;

  const handleDrawerClose = (_event: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && !dropBack) return;
    handleClose();
  };

  return (
    <Drawer
      sx={{ zIndex: drawerZIndex }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          maxWidth: '100%',
          marginTop: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      ModalProps={{
        keepMounted: false,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        sx: { zIndex: drawerZIndex },
      }}
      transitionDuration={300}>
      <Grid
        container
        className="content"
        sx={{
          height: '100%',
          width: '100%',
          minWidth: 0,
          flex: 1,
          flexWrap: 'nowrap',
          flexDirection: 'column',
        }}>
        <Grid
          sx={{
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            width: '100%',
            maxWidth: '100%',
            flexShrink: 0,
          }}
          container
          item
          alignItems="center"
          justifyContent="space-between"
          p={1.5}>
          <Grid item xs={closeButton ? 10 : 12} sx={{ minWidth: 0, pr: closeButton ? 1 : 0 }}>
            {typeof title === 'string' || typeof title === 'number' ? (
              <Typography
                fontWeight="500"
                fontSize="20px"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {title}
              </Typography>
            ) : (
              title
            )}
          </Grid>
          {closeButton && (
            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                size="small"
                color="default"
                className="deleteButton"
                onClick={handleClose}
                aria-label="close"
                sx={{ p: 0.5 }}>
                <HighlightOffOutlinedIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
        <Divider />
        <Grid
          item
          xs={12}
          sx={{
            px: noPadding ? 0 : 2,
            py: noPadding ? 0 : 2,
            flex: '1 1 auto',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: flexColumn ? 'hidden' : 'auto',
            ...(flexColumn && { display: 'flex', flexDirection: 'column' }),
          }}>
          {children}
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default DrawerMenu;
